import React, { useCallback, useEffect, useState } from 'react';
import { GridContainer, Grid } from '../../../shared/grid';
import { Formik, Form, } from 'formik';
import { Autocomplete, TextField } from '@mui/material';
import { ValidCharacters, errorResponse, truncateFourDecimals, validateCharacters, regex4decimals } from '../../../store/common';
import Button from '@mui/material/Button';
import '../FieldSample.scss';
import * as Yup from 'yup';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment-timezone';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useStoreContext } from '../../../store/context';
import { useAddAlert } from 'src/store/actions';
import { getArea, getCodes, getGroups, getSiteMetaData, getStatesDropdown } from 'src/apis/getFuelSampleCalls';
import { createUpdateSite } from 'src/apis/mutationsFuelSample';
import { alertMessages } from 'src/constants';


export const validationSchema = Yup.object().shape(
    {
      siteName: Yup.string().nullable()
        .required('Site Name Name is Required'),  
      latitude: Yup.string().nullable()
        .required('Latitude is Required').test('custom-validation', 'Invalid latitude', (value)=> validateLatitude(value)),  
      longitude: Yup.string().nullable()
        .required('Longitude is Required').test('custom-validation', 'Invalid longitude', (value)=> validateLongitude(value)),  
      elevation: Yup.string().nullable()
        .required('Elevation is Required'),  
      siteStartDate: Yup.string().nullable()
        .required('Site Start Date is Required'),  
        jurisdiction: Yup.object().nullable().shape({
          code_key: Yup.string()
            .required('Agency Landowner is Required'),
      }).required('Agency Landowner is Required'),
      remarks: Yup.string().nullable().max(1000, 'Remarks can only have 1,000 characters. This limit has been reached'),
      group: Yup.object().nullable().shape({
        label: Yup.string()
          .required('Group Name is Required'),
      }).required('Group Name is Required'),
      area: Yup.object().nullable().shape({
        label: Yup.string()
          .required('Area Name is Required'),
      }).required('Area Name is Required'),
      state: Yup.object().nullable().shape({
        label: Yup.string()
          .required('State is Required'),
      }).required('State is Required'),
      siteStatus: Yup.string().nullable()
        .required('Site Status is Required'),
      
    }
);

const Status=[
  { name: "Active", id: 1}, //can be[1=Active, 0=Inactive],
  { name: "Inactive", id: 0 },
]

type SiteProps ={
  setOpen: any,
  setSiteCreatedSuccess: any,
  editSite?: any,
}

const validateLatitude = (value: any): boolean =>{
  return (value) ? Number.isFinite(Number(value)) && Math.abs(value) <= 90:false ;
}

const validateLongitude = (value: any): boolean =>{
  return (value) ? Number.isFinite(Number(value)) && Math.abs(value) <= 180: false ; 
}

interface FormVals {
  siteId: number|null,
  siteName: string,
  longitude: string,
  latitude: string, 
  elevation: number | '',
  remarks: string,
  jurisdiction: any,
  siteStatus: string ,
  siteStartDate: string, // any
  area: any,
  group: any,
  state: any,
}

export const Site: React.FC<SiteProps> = ({setOpen, editSite, setSiteCreatedSuccess}) =>{
  const [nullJurisdiction] = useState({
    "code_key": '',
    "code_table_id": null,
    "code_value": null,
    "type": "Site.Jurisdiction"
})
  const [state, dispatch] = useStoreContext();
  const [errorDate, setErrorDate] = useState<string|null>(null);

  const [, setCallComplete] = useState<boolean>(true);

  const [states, setStates] = useState<any[]>([]);
  const [stateFailureCount, setStateFailureCount] = useState(0);

  const [areas, setAreas] = useState<any[]>([]);
  const [areasCallComplete, setAreasCallComplete] = useState<boolean>(true);
  const [areaFailureCount, setAreaFailureCount] = useState(0);

  const [groups, setGroups] = useState<any[]>([]);
  const [groupFailureCount, setGroupFailureCount] = useState(0);
  const [groupSelectedArea, setGroupSelectedArea] = useState<any>(null);
 
  const [jurisdictions, setJurisdictions] = useState<any[]>([]);
  const [jurisdictionError, setJurisdictionError] = useState<string>('')

  const addAlert = useAddAlert(dispatch);

  const [callEdit, setCallEdit] = useState<boolean>(false);

  
  const [initVals, setInitVals] = useState<FormVals>({ 
    siteId: null, 
    siteName: '',
    longitude: '-', 
    latitude: '', 
    elevation: '', 
    remarks: '', 
    jurisdiction: nullJurisdiction, 
    siteStatus: 'Active',
    group: null, 
    siteStartDate: '', 
    state: null, 
    area: null
  });

    //* this gets the response from the group call to populate the group dropdown*/
  const getGroupList = useCallback((request: any, areaIdParam: any) => {
    (async () => {
      try {
        const groups: any = await getGroups(request, areaIdParam);
        setCallComplete(true);
        const groupsUniqueName = [...new Map(groups.map(item =>
         [item['groupName'], item])).values()];
        setGroups(groupsUniqueName);
      }
      catch (error) {
        setGroupFailureCount(groupFailureCount + 1)
        errorResponse(error, addAlert, alertMessages.fieldSampleHomeData);
      }
    })();
  }, [ groupFailureCount, addAlert]);

  /* areas list to populate the area dropdown*/
  const getAreasList = useCallback((request:any, areaId?: any) => {
    (async () => {
      try {
        const areas: any = await getArea(request, areaId);
        setAreas(areas);
        setAreasCallComplete(true);
      } catch (error) {
        setAreasCallComplete(true);
        setAreaFailureCount(areaFailureCount + 1)
        errorResponse(error, addAlert, alertMessages.fieldSampleHomeData);
      }
    })();
  }, [setAreasCallComplete, setAreas, areaFailureCount, addAlert]);
  
  /*call made to get the site by siteId when it is on edit*/
  const getSiteId = useCallback((requestParam: any)=>{
    (async () => {
      try {
        const inputFuel = {
          startDate: null,
          endDate: null,
          fuelType: "ALL",
          fuelSubCategory: "ALL",
          fuelMethod: "ALL"
        }
        const sitePromise: any = await getSiteMetaData(requestParam.token, requestParam.addAlert, requestParam.siteId, inputFuel);
        if(sitePromise){
          /* THE FIND IS NEEDED BC THERE IS SOME BAD DATA IN THE DATABASE*/
          const foundDir = jurisdictions.find((j:any)=>j.code_key===sitePromise[0]?.jurisdiction)
          if(!foundDir){
            setJurisdictionError('Please select an Agency Landowner from the list')
          }
          else{
            setJurisdictionError('')
          }
          const siteToEdit ={ 
            siteId: sitePromise[0]?.siteId, 
            siteName: sitePromise[0]?.siteName,
            longitude: truncateFourDecimals(Number((sitePromise[0]?.longitude))).toString(), 
            latitude: truncateFourDecimals(Number((sitePromise[0]?.latitude))).toString(), 
            elevation: sitePromise[0]?.elevation, 
            remarks: sitePromise[0]?.remarks, 
            jurisdiction: foundDir? foundDir:nullJurisdiction, 
            siteStatus: sitePromise[0]?.siteStatus,
            group: { 
              label: sitePromise[0]?.groupName, 
              value: sitePromise[0]?.groupId, 
              areaId: sitePromise[0]?.areaId, 
              areaName: sitePromise[0]?.areaName
            }, 
            siteStartDate: sitePromise[0]?.siteStartDate, 
            state: {label: sitePromise[0]?.stateName, value: sitePromise[0]?.stateId}, 
            area: {label: sitePromise[0]?.areaName, value: sitePromise[0]?.areaId}
          }
          setInitVals(siteToEdit);
        }
      } catch (error) {
        console.log('error', error)
        errorResponse(error, addAlert, alertMessages.fieldSampleHomeData);
      }
    })();
  },[addAlert, jurisdictions, nullJurisdiction]);

  const getJurisdictionCall = useCallback((request:any) => {
    (async () => {
      try {
        const getCodesResponse: any = await getCodes(request);
        setJurisdictions(getCodesResponse)
      } catch (error) {
        errorResponse(error, addAlert, alertMessages.fieldSampleHomeData);
        console.log('error', error)
      }
    })();
  }, [addAlert]);

  /** Set Areas */
  useEffect(() => {
    if(areas?.length <= 0 && areaFailureCount <= 2 && areasCallComplete) {
      setAreasCallComplete(false);
      const requestVars ={
        returnAll: false,
        type: 'Site.jurisdiction'
      }
      getJurisdictionCall(requestVars)
      const requestAreaReq = {
        token: null,
        setAreaCallComplete: setAreasCallComplete,
        setAreaFailureCount: setAreaFailureCount,
        areaFailureCount: areaFailureCount,
        addAlert: useAddAlert
      };
      getAreasList(requestAreaReq);
      const requestGroup ={
        token: null,
        addAlert:addAlert,
        setCallComplete: setCallComplete,
        setGroupFailureCount: setGroupFailureCount,
        groupFailureCount: groupFailureCount
      }
      getGroupList(requestGroup, 'All');
    }
  }, [areas, getAreasList, setAreaFailureCount, areasCallComplete, areaFailureCount, addAlert, getGroupList, groupFailureCount, getJurisdictionCall]);

  useEffect(()=>{
    if(editSite){
      if(!callEdit && jurisdictions?.length>0){
        setCallEdit(true);
        const requestSite={
          addAlert: addAlert,
          token: null,
          siteId: editSite.siteId
        }
        getSiteId(requestSite)
      }
    }
    else{
      setInitVals({ 
        siteId: null, 
        siteName: '',
        longitude: '-', 
        latitude: '', 
        elevation: '', 
        remarks: '', 
        jurisdiction: nullJurisdiction, 
        siteStatus: 'Active',
        group: null, 
        siteStartDate: '', 
        state: null, 
        area: null
      })
    }
  },[editSite, addAlert, getSiteId, callEdit, jurisdictions?.length, nullJurisdiction]);
  
  const handleSubmit= (values:any)=>{
    (async()=>{
      try{
        const params = {
          siteId: values.siteId,
          siteStartDate: values.siteStartDate,
          siteStatus: values.siteStatus,
          siteName: values.siteName,
          groupId: values.group.value,
          stateId: values.state.value,
          jurisdiction: values.jurisdiction.code_key,
          latitude: Number(values.latitude),
          longitude: Number(values.longitude),
          elevation: Number(values.elevation),
          remarks: values.remarks,
          addAlert: addAlert
        }
        const returnSite = await createUpdateSite(null, addAlert, params);
        if(returnSite){
          setOpen(false);
          if(editSite){
            setSiteCreatedSuccess({newGroupAssiged: values.group.value, previousGroup: initVals.group.value});
          }
          else{
            setSiteCreatedSuccess({newGroupAssiged: values.group.value, previousGroup: null});
          }
        }
      } catch(error:any){
        if(error.toString().includes('timezone')){
          errorResponse(error, addAlert,  alertMessages.latlong);
        } else if(error.toString().includes('site already exist')) {
          errorResponse(error, addAlert, alertMessages.saveUniqueSite);
        }
        else{
          errorResponse(error, addAlert, alertMessages.saveSite);
        }
      }
    })();
  }

  const getAllStates = useCallback((request: any) => {
    (async () => {
      try {
        const states: any = await getStatesDropdown(request);
        setStates(states);
      } catch (error) {
        setStateFailureCount(stateFailureCount + 1)
        errorResponse(error, addAlert, alertMessages.fieldSampleHomeData);
      }
    })();
  }, [setStates, stateFailureCount, addAlert]);
    /** Set States */
    useEffect(() => {
      if(states?.length <= 0 && stateFailureCount <= 2) {
        const requestStateReq = {
          setStateCallComplete: setCallComplete,
          addAlert: useAddAlert,
          token: null,
          setStateFailureCount: setStateFailureCount,
          stateFailureCount: stateFailureCount
        };
        getAllStates(requestStateReq);
      }
    }, [states, getAllStates, setStateFailureCount, stateFailureCount]);

  return(
  <GridContainer className="removePadding smooth-scrollbar" style={{height: '85%', maxHeight: '95%', overflowX: 'hidden'}}>
    <Grid row gap="md" className='page-heading'>
      <Grid
        desktop={{col:12}}
        className="display-flex flex-row flex-align-center flex-justify margin-bottom-205 paddingForModal">
          {
            editSite ? (
              <span className="font-sans-lg text-bold">Edit Site</span>
            ):(
              <span className="font-sans-lg text-bold">Add a Site</span>
            )
          }
      </Grid>
    </Grid>
    <hr style={{width: '100%', marginBottom: 0}}></hr>

    <Formik
      initialValues={initVals}
      validateOnBlur={true}
      validateOnChange={true}
      validateOnMount={true}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={(values) => handleSubmit(values) }>
      {({
        values,
        errors,
        touched,
        handleSubmit,
        setFieldValue,
        handleBlur,
        isValid,
        setFieldTouched,
      }) => { 

          const setState=(newValue: string)=>{
            setFieldTouched('state');
            if(newValue?.length ===1 && /^\s/.test(newValue)){
              return
            }
            setFieldValue('state', newValue);

          };
          const setSiteStatus=(newValue: string)=>{
            setFieldTouched('siteStatus');
            if(newValue?.length ===1 && /^\s/.test(newValue)){
              return
            }
            setFieldValue('siteStatus', newValue);
          }

          const setSiteName =(newValue: string) =>{
            setFieldTouched('siteName');
            if(newValue?.length ===1 && /^\s/.test(newValue)){
              return
            }
            setFieldValue('siteName', newValue);
          };
          const setLatitude=(newValue: string)=>{
            setFieldTouched('latitude');
            if(regex4decimals.test(newValue) || newValue===''){
              setFieldValue('latitude', newValue);
            }
          }

          const setLongitude = (newValue: string) => {
            setFieldTouched('longitude');
            if (regex4decimals.test(newValue) || newValue === '') {
              setFieldValue('longitude', newValue);
            }
          }

          const elevationKeyDown = (e: any)=>{
            if(e.key ==='.' && (values.elevation.toString()).includes('.')){
              e.preventDefault();
            }
          }

          const setElevation=(newValue: string)=>{
            setFieldTouched('elevation');
            setFieldValue('elevation', newValue);
          }

          const setRemarks=(newValue: string)=>{
            setFieldTouched('remarks');
            if(newValue?.length ===1 && /^\s/.test(newValue)){
              return
            }
            setFieldValue('remarks', newValue);
          }

          const handleDate = (newValue: any) => {
            setFieldTouched('siteStartDate');
            if(newValue){
              setFieldValue('siteStartDate', moment.tz(newValue, state.userTimeZone.id[0]).toISOString());
            }
          }

          const setJurisdiction=(newValue: any)=>{
            setFieldTouched('jurisdiction');
            setFieldValue('jurisdiction', newValue);
          }

          const setGroup=(newValue: any)=>{
            setFieldTouched('group');
            setFieldValue('group', newValue)
          }

          const setArea=(newValue: any)=>{
            setFieldTouched('area');
            setFieldValue('area', newValue);
          }
          const handleElevation = (event:any)=>{
            const newval=event.target.value;
            if(/^\d*$/.test(newval)){
              setElevation(newval)
            }
          }
          
        return(
          <Form onSubmit={handleSubmit}>
            <div className="paddingForm">

              {/* 'SITE NAME' INPUT FIELD */}
              <Grid row desktop={{ col: 8 }}>
                <label id='siteNameLabel'>Site Name*
                <TextField
                  value={values.siteName}
                  onBlur={handleBlur}
                  error={(!!errors.siteName && !!touched.siteName) || (editSite && errors.siteName)}
                  helperText={(!!errors.siteName && !!touched.siteName) || (editSite && errors.siteName) ? errors.siteName : ''}
                  id='siteName'
                  type='string'
                  onInput={(e) => validateCharacters(e, ValidCharacters.TextField, ValidCharacters.Hyphen, ValidCharacters.NumberSign, ValidCharacters.Period)}
                  size='medium'
                  fullWidth
                  onChange={(e: any) => setSiteName(e.target.value)}
                  placeholder='Input Site Name'
                />
                </label>
              </Grid>


              <Grid row gap="md">
              {/* 'LATITUDE' INPUT FIELD */}
              <Grid row desktop={{ col: 4 }}>
                <label id='latitudeLabel'>Latitude*
                <TextField
                  value={values.latitude}
                  onBlur={handleBlur}
                  error={(!!errors.latitude && !!touched.latitude) || (editSite && errors.latitude)}
                  helperText={(!!errors.latitude && !!touched.latitude) || (editSite && errors.latitude) ? errors.latitude : ''}
                  id='latitude'
                  size='medium'
                  fullWidth
                  onChange={(e: any) => setLatitude(e.target.value)}
                  placeholder='Input Latitude'
                />
                </label>
              </Grid>
              
              {/* 'LONGITUDE' INPUT FIELD */}
              <Grid row desktop={{ col: 4 }}>
                <label id='longitudeLabel'>Longitude*
                <TextField
                  value={values.longitude}
                  onBlur={handleBlur}
                  error={(!!errors.longitude && !!touched.longitude) || (editSite && errors.longitude)}
                  helperText={(!!errors.longitude && !!touched.longitude) || (editSite && errors.longitude) ? errors.longitude : ''}
                  id='longitude'
                  size='medium'
                  fullWidth
                  onChange={(e: any) => setLongitude(e.target.value)}
                  placeholder='Input Longitude'
                />
                </label>
              </Grid>
              </Grid>

                {/* 'ELEVATION' INPUT FIELD */}
                <Grid row desktop={{ col: 7 }}>
                <label id='elevationLabel'>Elevation*
                <TextField
                  value={values.elevation? values.elevation : ''}
                  onBlur={handleBlur}
                  error={(!!errors.elevation && !!touched.elevation) || (editSite && errors.elevation)}
                  helperText={(!!errors.elevation && !!touched.elevation) || (editSite && errors.elevation) ? errors.elevation : ''}
                  id='elevation'
                  type='number'
                  onKeyDown={elevationKeyDown}
                  size='medium'
                  fullWidth
                  onInput={(e) => handleElevation(e)}
                  onChange={(e: any) => handleElevation(e)}
                  placeholder='Enter Elevation'
                />
                </label>
              </Grid>

                {/* 'REMARKS' INPUT FIELD */}
                <Grid row desktop={{ col: 10 }}>
                <label id='remarksLabel'>Remarks
                <TextField
                  value={values.remarks}
                  onBlur={handleBlur}
                  error={!!errors.remarks}
                  helperText={!!errors.remarks ? errors.remarks : ''}
                  id='remarks'
                  type='string'
                  size='medium'
                  fullWidth
                  onChange={(e: any) => setRemarks(e.target.value)}
                  InputProps={{
                    inputProps: { maxLength: 1000 }
                  }}
                  placeholder='Enter Remarks'
                />
                </label>
              </Grid>

              {/* 'SITE START DATE' INPUT FIELD */}
              <Grid row gap="md">
              <Grid desktop={{col:4}}>
                  <LocalizationProvider dateAdapter={AdapterMoment} dateLibInstance={moment}>
                  <label htmlFor="siteStartDateLabel" id='siteStartDateLabel'>Site Start Date*
                    <DatePicker
                      disableFuture
                      value={ values.siteStartDate ? moment(values.siteStartDate) : null }
                      views={['year', 'month','day']}
                      format="MM/DD/YYYY"
                      className='width10'
                      timezone={state.userTimeZone.id[0]}
                      onChange={(newValue)=>{handleDate(newValue)}}
                      slotProps={{
                        textField: {
                          error: errorDate? true:false,
                          helperText: errorDate,
                          placeholder: "Site Start Date",
                          id: 'siteStartDateLabel'
                        },
                      }}
                      onError={(reason)=>{
                        if(reason===null){
                            setErrorDate(null)
                        }
                        else if(reason === 'disableFuture'){
                          setErrorDate('Date & Time are in the Future');
                        }
                        else if(reason ==='invalidDate'){
                          if(touched.siteStartDate){
                            setErrorDate('Invalid Date');
                          }
                        }
                        else{
                            setErrorDate('An Error on Date & Time')
                        }            
                    }}
                    
                    /> 
                    </label>

                  </LocalizationProvider>
                  <span style={{color: '#d32f2f'}}>{(!!errors.siteStartDate && !!touched.siteStartDate) || (editSite && errors.siteStartDate)? errors.siteStartDate: (errorDate? errorDate: '')}</span>

                </Grid>
                </Grid>

                {/* 'AGENCY LANDOWNER' SEARCH FIELD */}
                <Grid row desktop={{ col: 7 }}>
                <label id='jurisdiction' htmlFor='jurisdiction'>Agency Landowner*
                  <div className='searchAreas' style={{ width: '100%' }}>
                    <i className="fas fa-search" />
                    <Autocomplete
                      id='jurisdiction'
                      onBlur={()=>{setFieldTouched('jurisdiction');}}
                      onChange={(_event: any, newValue: any) => {
                        setJurisdiction(newValue);
                        const findJurisdiction = jurisdictions.find((jurisdiction: any)=> jurisdiction.code_key === newValue?.code_key);
                        if(!findJurisdiction){
                          setJurisdictionError('Please select an Agency Landowner from the list')
                        }
                        else{
                          setJurisdictionError('')
                        }
                      }}
                      style={{ width: '100%' }}
                      value={values.jurisdiction}
                      options={jurisdictions? jurisdictions: []}
                      getOptionLabel={option=>option?.code_key=== null? null: option.code_key}
                      renderInput={(params) => (
                        <TextField
                          placeholder='Search Agency Landowner'
                          onInput={(e) => validateCharacters(e, ValidCharacters.TextField, '-', '&')}
                          style={{ width: '100%' }}
                          onBlur={()=>{setFieldTouched('jurisdiction');}}
                          aria-label={'Search Agency Landowner'}
                          data-testid={'jurisdiction'}
                          error={(!!errors.jurisdiction && !!touched.jurisdiction) || jurisdictionError?.length>0}
                          helperText={(!!errors.jurisdiction && !!touched.jurisdiction ) ? 'Agency Landowner is Required' : jurisdictionError?.length>0? jurisdictionError: ''}
                          {...params}
                          InputProps={{
                            ...params.InputProps,
                            type: 'search',
                          }}
                        />
                      )} 
                    />
                  </div>
                </label>
              </Grid>


                {/***  'GROUP NAME' SEARCH FIELD */}
                <Grid row desktop={{ col: 7 }}>
                <label id='groupNameLabel' htmlFor='groupNameLabel'>Group Name*
                <div className='searchAreas' style={{ width: '100%' }}>
                  <i className="fas fa-search" />
                  <Autocomplete
                    id='groupNameLabel'
                    onBlur={()=>{setFieldTouched('group');}}
                    noOptionsText={
                      <div className='no-results'>
                      <div>No group under that area.</div>
                      </div>
                    }
                    onChange={(_event: any, newValue: any) => {
                      if(newValue){
                        setGroup(newValue);
                        setGroupSelectedArea([{label: newValue?.areaName, value: newValue?.areaId}])
                        setArea({label: newValue?.areaName, value: newValue?.areaId})
                      }
                      else{
                        setGroup(newValue);
                        setGroupSelectedArea(null)
                      }
                    }}
                    style={{ width: '100%' }}
                    value={values.group}
                    options={groups?.map((group) => ({ label: group.groupName, value: group.groupId, areaId: group.area.areaId, areaName: group.area.areaName }))}
                    renderInput={(params) => (
                      <TextField
                        placeholder='Search Group'
                        onInput={(e) => validateCharacters(e, ValidCharacters.TextField)}
                        style={{ width: '100%' }}
                        onBlur={()=>{setFieldTouched('group');}}
                        aria-label={'Search group'}
                        data-testid={'search-group'}
                        error={(!!errors.group && !!touched.group) || (editSite && errors.group)}
                        helperText={(!!errors.group && !!touched.group) || (editSite && errors.group) ? errors.group : ''}
                        {...params}
                        InputProps={{
                          ...params.InputProps,
                          type: 'search',
                        }}
                      />
                    )}
                  />
                </div>
                </label>
              </Grid>

              {/***  'AREA NAME' READ-ONLY FIELD */}
              <Grid row desktop={{ col: 7 }}>
                <label id='areaNameLabel' htmlFor='areaNameLabel'>Area Name*
                <div className='searchAreas' style={{ width: '100%' }}>
                  <i className="fas fa-search" />
                  <Autocomplete
                    id='areaNameLabel'
                    freeSolo
                    onBlur={() =>{setFieldTouched('area');}}
                    onChange={(_event: any, newValue: any) => {
                      if(newValue){
                        setArea(newValue);
                        const requestGroup ={
                          token: null,
                          addAlert:addAlert,
                          setCallComplete: setCallComplete,
                          setGroupFailureCount: setGroupFailureCount,
                          groupFailureCount: groupFailureCount
                        }
                        getGroupList(requestGroup, newValue.label);
                      }
                      else{
                        setArea(newValue);
                      }
                    }}
                    style={{ width: '100%' }}
                    value={values.area}
                    options={groupSelectedArea? (groupSelectedArea):(areas?.map((area) => ({ label: area.areaName, value: area.areaId })))}
                    renderInput={(params) => (
                      <TextField
                        placeholder='Search Area'
                        onInput={(e) => validateCharacters(e, ValidCharacters.TextField)}
                        onBlur={() =>{setFieldTouched('area');}}
                        style={{ width: '100%' }}
                        aria-label={'Search Area'}
                        data-testid={'search-area'}
                        error={(!!errors.area && !!touched.area) || (editSite && errors.area)}
                        helperText={(!!errors.area && !!touched.area) || (editSite && errors.area) ? errors.area : ''}
                        {...params}
                        InputProps={{
                          ...params.InputProps,
                          type: 'search',
                        }}
                      />
                       )}
                  />
                </div>
                </label>
              </Grid>


          {/***  'STATUS' COMBO BOX ***/}
              <Grid row desktop={{ col: 10 }}>
                <label id='siteStatus' htmlFor='siteStatus'>Status*
                <div className ="searchArea" style={{ width: '40%' }}>
                  <Autocomplete
                    id='siteStatus'
                    className='height-55'
                    freeSolo
                    onChange={(event: any, newValue: any) => {
                      setSiteStatus(newValue);
                    }}
                    onBlur={() =>{setFieldTouched('siteStatus');}}
                    style={{ width: '100%' }}
                    value={values.siteStatus}
                    options={Status.map((status) => status.name)}
                    renderInput={(params) => (
                      <TextField
                        placeholder='Select Status'
                        onInput={(e) => {validateCharacters(e, ValidCharacters.Alpha, ValidCharacters.Space)}}
                        style={{ width: '100%' }}
                        onBlur={() =>{setFieldTouched('siteStatus');}}
                        error={(!!errors.siteStatus && !!touched.siteStatus) || (editSite && errors.siteStatus)}
                        helperText={(!!errors.siteStatus && !!touched.siteStatus)|| (editSite && errors.siteStatus) ? errors.siteStatus : ''}
                        aria-label={'Search Status'}
                        data-testid={'search-start'}
                        {...params}
                        InputProps={{
                          ...params.InputProps,
                          type: 'search',
                        }}
                      />
                    )}
                  />
                </div>
                </label>

              </Grid>
              {/* State Search*/}
               <Grid row desktop={{ col: 12 }}>
                <label id='statesName' htmlFor='statesName'>State *
                <div className='searchStates' style={{ width: '40%'}}>
                  <i className="fas fa-search" />
                  <Autocomplete
                    id='statesName'
                    onChange={(event: any, newValue: any) => {
                      setState(newValue);
                    }}
                    
                    onBlur={handleBlur}
                    style={{ width: '100%' }}
                    value={values.state}
                    options={states.map((state) => ({label: state.state_name, value: state.state_id}))}
                    renderInput={(params) => (
                      <TextField
                        placeholder='Search States'
                        onInput={(e)=>validateCharacters(e, ValidCharacters.Alpha, ValidCharacters.Space, ',')}
                        style={{ width: '100%' }}
                        onBlur={handleBlur}
                        error={(!!errors.state && !!touched.state) || (errors.state && editSite)}
                        helperText={(!!errors.state && !!touched.state) || (errors.state && editSite) ? errors.state : ''}
                        aria-label={'Search State'}
                        data-testid={'search-start'}
                        {...params}
                        InputProps={{
                          ...params.InputProps,
                          type: 'search',
                        }}
                      />
                    )}
                  />
                </div>
                </label>
              </Grid>

            </div>

            <Grid row className='modalBottomDiv'>
              <Grid desktop={{col:10}} className="modalCancelDiv">
                <Button variant="outlined" style={{backgroundColor: 'white'}} onClick={()=>{setOpen(false)}}>
                  Cancel
                </Button>
              </Grid> 
              {
                editSite? (
                  <Grid desktop={{col:2}} className='modalSubmitDiv'>
                    <Button variant="contained" type="submit" aria-disabled={!isValid || jurisdictionError?.length>0}>
                      Save
                    </Button>
                  </Grid>
                ) : (
                  <Grid desktop={{col:2}} className='modalSubmitDiv'>
                    <Button variant="contained" type="submit" aria-disabled={!isValid || jurisdictionError?.length>0}>
                      Add
                    </Button>
                  </Grid>
                )
              }
            </Grid>
          </Form>
      )}}
    </Formik>
  </GridContainer>
  )
}